import { logger } from "@justhome/common";
import {
  LeadAction,
  LeadCreationResponse,
  usePostLeadAction,
} from "@justhome/api-core";
import { Button, Icon, Modal } from "@justhome/react-ui";
import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface InterestRateModalProps {
  isOpen: boolean;
  offerId?: string;
  onClose?: VoidFunction;
  lead: LeadCreationResponse;
}

const InterestRateModal: FC<InterestRateModalProps> = ({
  isOpen,
  onClose,
  lead,
  offerId,
}) => {
  const { t } = useTranslation();
  const { mutate, isLoading, isSuccess, reset, isError } = usePostLeadAction({
    onError(error: any) {
      logger.error("Error while posting a lead.", error);
    },
  });

  const handleSubmit = () => {
    const body: LeadAction = {
      applicationId: lead.applicationId!,
      action: "CONTACT_EMAIL_SELECTED_OFFER",
      offerPreviewId: offerId,
    };

    mutate({ body, pathParams: { leadId: lead.id } });
  };

  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  return (
    <Modal
      isOpen={isOpen}
      className="relative"
      setIsOpen={onClose}
      maxWidth={600}
    >
      <Button
        disabled={isLoading}
        onClick={onClose}
        iconStart="Close"
        size="sm"
        variant="ghost"
        className="absolute top-2xs right-2xs"
      />
      {isError && (
        <>
          <Modal.Title className="text-app-h3 mt-s">
            {t("interestRateModal.errorTitle")}
          </Modal.Title>
          <Modal.Description>
            <div className="flex items-center rounded-s border border-error-200 bg-error-50 px-s py-2xs">
              <Icon
                type="Attention"
                className="text-error-700 mr-2xs"
                size="16"
              />
              {t("interestRateModal.errorMessage")}
            </div>
          </Modal.Description>
          <Button
            size="sm"
            variant="outline_primary"
            iconStart="Left"
            onClick={onClose}
          >
            {t("interestRateModal.backToResults")}
          </Button>
        </>
      )}
      {isSuccess && (
        <>
          <Modal.Title className="text-app-h3 mt-s">
            {t("interestRateModal.thankYouTitle")}
          </Modal.Title>
          <Modal.Description>
            {t("interestRateModal.thankYouMessage")}
          </Modal.Description>
          <Button
            size="sm"
            variant="outline_primary"
            iconStart="Left"
            onClick={onClose}
          >
            {t("interestRateModal.backToResults")}
          </Button>
        </>
      )}
      {!isSuccess && !isError ? (
        <>
          <Modal.Title className="text-app-h3 mt-s">
            {t("interestRateModal.title")}
          </Modal.Title>
          <Modal.Description>
            {t("interestRateModal.description")}
          </Modal.Description>
          <Button
            size="sm"
            onClick={handleSubmit}
            isLoading={isLoading}
            iconStart="Check"
          >
            {t("interestRateModal.confirmReserve")}
          </Button>
        </>
      ) : null}
    </Modal>
  );
};

export default InterestRateModal;
